import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/marketing/components/ui/popover";
import { Input } from "@/marketing/components/ui/input";
import { Separator } from "@/marketing/components/ui/separator";
import { List, Search, ChevronDown, Plus, Check, Trash2 } from "lucide-react";
import { useListStore } from "@/marketing/stores/useListStore";
import { INFLUENCER_QUERIES } from "@/marketing/infrastructure/influencer/utils/queries";
import { INFLUENCER_KEYS } from "@/marketing/infrastructure/influencer/utils/keys";
import { influencerAPIClient } from "@/marketing/infrastructure/influencer/influencerAPIClient";

export function ListSelector() {
  const activeListId = useListStore((s) => s.activeListId);
  const setActiveList = useListStore((s) => s.setActiveList);

  const { data: lists = [] } = useQuery(INFLUENCER_QUERIES.lists());
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (name: string) => influencerAPIClient.createList(name),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
      setActiveList(newList.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (listId: string) => influencerAPIClient.deleteList(listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
    },
  });

  // Exclude "Rejected" list — it's shown in the rejected tab
  const selectableLists = lists.filter(
    (l) => l.name.toLowerCase() !== "rejected",
  );
  const canDelete = selectableLists.length > 1;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newListName, setNewListName] = useState("");

  const activeList = selectableLists.find((l) => l.id === activeListId);

  const filteredLists = selectableLists.filter((l) =>
    l.name.toLowerCase().includes(search.toLowerCase()),
  );

  async function handleCreateList() {
    const trimmed = newListName.trim();
    if (!trimmed) return;
    await createMutation.mutateAsync(trimmed);
    setNewListName("");
    setIsCreating(false);
  }

  function handleSelectList(id: string) {
    setActiveList(id);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-neutral-100 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-foreground hover:tw-bg-neutral-200 tw-transition-colors"
          type="button"
        >
          <List className="tw-h-4 tw-w-4" />
          {activeList?.name ?? "Select list"}
          <ChevronDown className="tw-h-3.5 tw-w-3.5 tw-opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="tw-w-[260px] tw-p-0" align="start">
        {/* Search */}
        <div className="tw-p-2">
          <div className="tw-relative">
            <Search className="tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-muted-foreground" />
            <Input
              placeholder="Search lists..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="tw-h-8 tw-pl-8 tw-text-sm"
            />
          </div>
        </div>
        <Separator />

        {/* List items */}
        <div className="tw-max-h-[200px] tw-overflow-y-auto tw-p-1">
          {filteredLists.map((list) => {
            const isActive = list.id === activeListId;
            return (
              <button
                key={list.id}
                type="button"
                className={`tw-group tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-transition-colors ${
                  isActive
                    ? "tw-bg-[#2563EB10] tw-text-[#2563EB]"
                    : "tw-text-foreground hover:tw-bg-muted"
                }`}
                onClick={() => handleSelectList(list.id)}
              >
                <List className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-flex-1 tw-truncate tw-text-left">{list.name}</span>
                <span className="tw-text-xs tw-text-muted-foreground">
                  {list.memberCount}
                </span>
                {isActive && <Check className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />}
                {canDelete && (
                  <button
                    type="button"
                    className="tw-shrink-0 tw-rounded tw-p-0.5 tw-text-muted-foreground tw-opacity-0 group-hover:tw-opacity-100 hover:tw-text-red-600 hover:tw-bg-red-50 tw-transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(list.id);
                    }}
                    aria-label={`Delete ${list.name}`}
                  >
                    <Trash2 className="tw-h-3 tw-w-3" />
                  </button>
                )}
              </button>
            );
          })}
          {filteredLists.length === 0 && (
            <p className="tw-px-2 tw-py-4 tw-text-center tw-text-xs tw-text-muted-foreground">
              No lists found.
            </p>
          )}
        </div>

        <Separator />

        {/* Create new list */}
        <div className="tw-p-1">
          {isCreating ? (
            <div className="tw-px-2 tw-py-1.5">
              <Input
                autoFocus
                placeholder="List name..."
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateList();
                  if (e.key === "Escape") {
                    setIsCreating(false);
                    setNewListName("");
                  }
                }}
                className="tw-h-8 tw-text-sm"
              />
            </div>
          ) : (
            <button
              type="button"
              className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm tw-text-[#2563EB] hover:tw-bg-muted tw-transition-colors"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="tw-h-3.5 tw-w-3.5" />
              Create new list
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
